import type { SignDocumentApi } from "@/Modules/SignDocuments/Models/Api";
import type {
  CreateTemplateDocumentQueryApi,
  TemplateDocumentApi,
  TemplateDocumentVersionApi,
  TemplateUserStatsApi,
} from "@/Modules/TemplateDocuments/Models/Api";

import { get2, post2, patch2, remove2 } from "@/Services/TherapyApi/utils/FunctionalTherapyApi";

export type TemplateDocumenApiUpdatePayload = Partial<
  Pick<TemplateDocumentApi, "document_type" | "is_required" | "status">
>;

export const getAllTemplateDocuments = async (): Promise<TemplateDocumentApi[]> => {
  return get2<void, TemplateDocumentApi[]>("v1.0/template_documents");
};

export const createDocumentTemplate = async (
  data: CreateTemplateDocumentQueryApi
): Promise<TemplateDocumentApi> => {
  return post2<CreateTemplateDocumentQueryApi, TemplateDocumentApi>("v1.0/template_documents", data);
};

export const getTemplateDocument = async (uuid: string): Promise<TemplateDocumentApi> => {
  return get2<void, TemplateDocumentApi>(`v1.0/template_documents/${uuid}`);
};

export const updateTemplateDocument = async (
  uuid: string,
  payload: TemplateDocumenApiUpdatePayload
): Promise<TemplateDocumentApi> => {
  return patch2<TemplateDocumenApiUpdatePayload, TemplateDocumentApi>(
    `v1.0/template_documents/${uuid}`,
    payload
  );
};

export const duplicateTemplateDocument = async (uuid: string): Promise<TemplateDocumentApi> => {
  return post2<void, TemplateDocumentApi>(`v1.0/template_documents/${uuid}`);
};

export const removeTemplateDocument = async (uuid: string): Promise<TemplateDocumentApi> => {
  return remove2<void, TemplateDocumentApi>(`v1.0/template_documents/${uuid}`);
};

export const getAllVersions = async (uuid: string): Promise<TemplateDocumentVersionApi[]> => {
  return get2<void, TemplateDocumentVersionApi[]>(`v1.0/template_documents/${uuid}/versions`);
};

export const createVersion = async (
  uuid: string,
  data: Pick<TemplateDocumentVersionApi, "content" | "title">
): Promise<TemplateDocumentApi> => {
  return post2<Pick<TemplateDocumentVersionApi, "content" | "title">, TemplateDocumentApi>(
    `v1.0/template_documents/${uuid}/versions`,
    data
  );
};

export const selectVersion = async (
  templateDocumentId: string,
  versionId: string
): Promise<TemplateDocumentApi> => {
  return post2<void, TemplateDocumentApi>(
    `v1.0/template_documents/${templateDocumentId}/versions/${versionId}`
  );
};

export const sendToSign = async (uuid: string, userUuid: string): Promise<SignDocumentApi> => {
  return post2<void, SignDocumentApi>(`v1.0/template_documents/${uuid}/sign/${userUuid}`);
};

export const getTemplateStatsByUserUuid = async (userUuid: string): Promise<TemplateUserStatsApi> => {
  return get2<void, TemplateUserStatsApi>(`v1.0/template_documents/users/${userUuid}/stats`);
};

export const sendRequiredTemplates = async (userUuid: string): Promise<TemplateDocumentApi[]> => {
  return post2<void, TemplateDocumentApi[]>(`v1.0/template_documents/users/${userUuid}/required`);
};

export default {
  getAllTemplateDocuments,
  createDocumentTemplate,
  updateTemplateDocument,
  removeTemplateDocument,
  getAllVersions,
  createVersion,
  selectVersion,
};
